/**
 * @typedef {import('shared/chat/emoji-picker.js').default} EmojiPicker
 * @typedef {import('shared/chat/emoji-dropdown.js').default} EmojiDropdown
 * @typedef {import('./message-form.js').default} MessageForm
 */
import { _converse, api } from '@converse/headless';
import { CustomElement } from 'shared/components/element.js';
import tplBottomPanel from './templates/bottom-panel.js';
import { clearMessages } from './utils.js';
import './message-form.js';

import './styles/chat-bottom-panel.scss';

export default class ChatBottomPanel extends CustomElement {
    constructor() {
        super();
        this.model = null;
    }

    static get properties() {
        return {
            model: { type: Object },
        };
    }

    async connectedCallback() {
        super.connectedCallback();
        await this.initialize();
        this.requestUpdate();
    }

    async initialize() {
        await this.model.initialized;

        // Базовые слушатели
        this.listenTo(this.model, 'emoji-picker-autocomplete', this.autocompleteInPicker);
        this.listenTo(this.model, 'change:reply_to', () => this.requestUpdate());
        this.listenTo(this.model, 'change:omemo_key_exchange', () => this.requestUpdate());
        this.addEventListener('emojipickerblur', () =>
            /** @type {HTMLElement} */ (this.querySelector('.chat-textarea')).focus(),
        );

        // 1. Главный слушатель входящих сообщений
        // `document.hidden` is checked in addition to the model's own `hidden`
        // flag: clearing here sends a read marker, and nothing may be marked read
        // while the browser tab is in the background (rechecked inside the timeout).
        this.listenTo(this.model, 'change:num_unread', () => {
            if (!this.model.get('hidden') && !(document.hidden || !document.hasFocus())) {
                // Используем setTimeout, чтобы дать Converse завершить рендер сообщения и прокрутку
                setTimeout(() => {
                    // Если флаг scrolled в false, значит мы внизу — чистим счетчик
                    if (!this.model.ui.get('scrolled') && !(document.hidden || !document.hasFocus())) {
                        this.clearUnread();
                    }
                }, 100);
            }
            this.requestUpdate();
        });

        // 2. Слушатель скролла (когда пользователь докрутил вниз сам)
        this.listenTo(this.model.ui, 'change:scrolled', (ui_model, scrolled) => {
            if (!scrolled && !this.model.get('hidden') && !(document.hidden || !document.hasFocus())) {
                this.clearUnread();
            }
            this.requestUpdate();
        });
    }

    render() {
        if (!this.model) return '';
        return tplBottomPanel({
            'model': this.model,
            'viewUnreadMessages': (ev) => this.viewUnreadMessages(ev),
        });
    }

    async clearUnread() {
        // Clearing sends a read marker (below); never while the tab is hidden.
        if (document.hidden || !document.hasFocus()) {
            return;
        }
        const jid = this.model.get('jid');
        const num = this.model.get('num_unread') || 0;
        const num_gen = this.model.get('num_unread_general') || 0;

        // Если мы в методе очистки, значит плашка "Новые сообщения" не должна висеть
        if (this.model.ui.get('scrolled')) {
            this.model.ui.set({ 'scrolled': false });
        }

        if (num > 0 || num_gen > 0) {
            console.log(`=== SONATA: Force clear for ${jid} ===`);

            // 1. Пытаемся отправить маркер прочтения на сервер
            try {
                const lastMsg = this.model.messages?.last();
                if (lastMsg && typeof this.model.sendMarkerForMessage === 'function') {
                    this.model.sendMarkerForMessage(lastMsg);
                }
            } catch (e) {
                console.warn('Could not send XMPP marker', e);
            }

            const clearData = {
                'num_unread': 0,
                'num_unread_general': 0,
                'first_unread_id': null,
            };

            // 2. Сохраняем в текущую модель (компонент обновится)
            this.model.save(clearData);

            // 3. Синхронизируем с ростером (бадж в списке контактов)
            const contact = await api.contacts.get(jid);
            if (contact) {
                contact.save({ 'num_unread': 0 }, { silent: false });
            }

            // 4. Синхронизируем с глобальной коллекцией (чтобы Tab/ControlBox увидел изменения)
            const chatbox = _converse.chatboxes.get(jid);
            if (chatbox) {
                chatbox.save(clearData, { silent: false });
                // Пинаем ивенты, которые слушает твой ControlBox
                chatbox.trigger('change:num_unread');
                chatbox.trigger('change');
            }

            // 5. Глобальное обновление общего счетчика
            _converse.chatboxes.trigger('change:num_unread');

            this.requestUpdate();
        }
    }

    viewUnreadMessages(ev) {
        ev?.preventDefault?.();
        // Сбрасываем скролл, это автоматически триггернет очистку через слушатель
        this.model.ui.set({ 'scrolled': false });
        this.clearUnread();
        this.requestUpdate();
    }

    onDragOver(ev) {
        ev.preventDefault();
    }

    clearMessages(ev) {
        ev?.preventDefault?.();
        clearMessages(this.model);
    }

    async autocompleteInPicker(ev) {
        const { target: input, value } = ev;
        await api.emojis.initialize();
        const emoji_picker = /** @type {EmojiPicker} */ (this.querySelector('converse-emoji-picker'));
        if (emoji_picker) {
            emoji_picker.state.set({
                ac_position: input.selectionStart,
                autocompleting: value,
                query: value,
            });
            const emoji_dropdown = /** @type {EmojiDropdown} */ (this.querySelector('converse-emoji-dropdown'));
            emoji_dropdown?.dropdown.show();
        }
    }
}

api.elements.define('converse-chat-bottom-panel', ChatBottomPanel);
